import {
  type GetPaginationSessionsParams,
  type GetSessionsAssetsResponse,
  useInfiniteScrollFromCache,
  useLazyGetSessionsAssetsQuery,
} from '@carboncopyco/data';
import { Modal, TextSubHeader } from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import { useEffect, useMemo } from 'react';
import { FixedTable } from '../../../../FixedTable';
import { AssetTableSkeleton } from '../../../AssetTableSkeleton';
import { useDashboardProps } from '../../../DashboardContext';
import { DemoRowWrapper } from '../../DemoRowWrapper';
import { getCols } from './cols';

const cols = getCols(true);

export const ViewAllDemosModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { organizationId, selectedWorkspaceIds, timeRange, short, internal } =
    useDashboardProps();

  const fetchArgs = useMemo<GetPaginationSessionsParams>(
    () => ({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes: ['play', 'managed_presentation', 'shared_presentation'],
    }),
    [organizationId, short, internal, timeRange, selectedWorkspaceIds]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScrollFromCache<
    GetSessionsAssetsResponse,
    GetPaginationSessionsParams
  >(useLazyGetSessionsAssetsQuery, fetchArgs);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs, fetchFirstBatch]);

  return (
    <Modal
      containerClass="px-0 pb-0"
      floatingCloseButton
      title={
        <div className="flex items-center gap-2 pb-7 px-6">
          <TextSubHeader
            color="text-ds-natural-600"
            label="Demos"
          />
          {!!totalSize && (
            <TextSubHeader
              label={`(${totalSize})`}
              color="text-ds-natural-300"
            />
          )}
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <FixedTable<GetSessionsAssetsResponse>
        cols={cols}
        data={items}
        isLoading={isLoading}
        loader={<AssetTableSkeleton rows={8} />}
        isLoadingBatch={isLoadingBatch}
        handleFetchNextBatch={fetchNextBatch}
        error={error}
        infiniteListMaxHeight={600}
        rowWrapper={DemoRowWrapper}
        sideSpacing
      />
    </Modal>
  );
};
